// Finding the right explanation for a question the learner typed.
//
// This is the part of the tutor that decides whether a knowledge base can stand in for a model. A
// live model always produces SOMETHING; an index has to find the right entry or admit it cannot,
// and admitting it is the harder half to get right. An index that answers everything by returning
// its nearest keyword match is worse than no tutor at all — a learner asking about grammar cannot
// tell a confidently wrong answer from a correct one, which is precisely why they asked.
//
// So scoring is deliberately blunt and the floor is deliberately high. Matching is over curated
// phrasings (`ask`) and the Chinese words an entry is about, not over the prose of the answer:
// bodies share ordinary words with every question ever typed, and letting them score turns the
// index into a thesaurus.

import type { TutorEntry } from './types.js';

export interface Hit {
  entry: TutorEntry;
  score: number;
}

/** Words too common to carry meaning in a question about grammar. */
const STOP = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'between', 'but', 'by', 'can', 'chinese', 'difference',
  'different', 'do', 'does', 'for', 'from', 'has', 'have', 'how', 'i', 'in', 'is', 'it', 'its',
  'mandarin', 'me', 'mean', 'means', 'my', 'of', 'on', 'or', 'say', 'should', 'so', 'that', 'the',
  'their', 'them', 'there', 'these', 'they', 'this', 'to', 'use', 'used', 'using', 'was', 'what',
  'when', 'where', 'which', 'why', 'with', 'work', 'works', 'would', 'you', 'your',
]);

/**
 * Split a query into comparable pieces: latin words, and Han characters one by one.
 *
 * Stop words are dropped for the loose-overlap scoring only. They must NOT gate the rest: "how to
 * say can" is every one of them, so an early return on an empty token list scored that question
 * zero against every entry — including the one whose curated phrasing it matched exactly.
 */
export function tokenise(text: string): { words: string[]; han: string[] } {
  const lower = text.toLowerCase();
  const words = (lower.match(/[a-z]+/gu) ?? []).filter((w) => w.length > 1 && !STOP.has(w));
  const han = [...new Set(lower.match(/\p{Script=Han}/gu) ?? [])];
  return { words, han };
}

/**
 * Whether `phrase` appears in `text` as whole words rather than as a run of letters.
 *
 * Plain substring matching was wrong in both directions and quietly so. "can you quiz me on food
 * vocabulary" matched the modals entry because its phrasing "can" sits inside "can you"; "how do I
 * compare two things" matched the progressive entry because its phrasing "ing" sits inside
 * "things". Guarding by length instead of by boundaries was the other half of the mistake: it made
 * the two-letter phrasings — "le", "if", "ma", the very things a beginner types on their own —
 * unmatchable, so "how do I say if" found nothing at all. Boundaries fix both, and let short
 * phrasings back in safely.
 *
 * Han characters have no word boundaries to find, so those are matched directly.
 */
function containsPhrase(text: string, phrase: string): boolean {
  if (!phrase) return false;
  if (!/[a-z]/u.test(phrase)) return text.includes(phrase);
  const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
  return new RegExp(`(^|[^a-z])${escaped}($|[^a-z])`, 'u').test(text);
}

/**
 * How well one entry answers one query.
 *
 * The weights encode what actually identifies an entry. A Chinese character the entry is ABOUT is
 * the strongest signal there is — someone who types 把 wants the 把 entry and nothing else. A
 * curated phrasing matching as a whole phrase is next. Loose word overlap is worth little on its
 * own and is there only to break ties between entries that already matched something real.
 */
export function scoreEntry(entry: TutorEntry, query: string): number {
  const { words, han } = tokenise(query);
  let score = 0;

  // The Chinese this entry is about.
  //
  // A WHOLE WORD appearing in the question is the strong signal; loose character overlap is nearly
  // none. Scoring characters alike made every entry containing 一 — 一定, 一点, 一直 — a confident
  // answer to "why cant I say 一书", because the query happens to contain that one character. The
  // longer the entry's word, the less any single character of it says.
  // Weighted by how long the matching word is, because a one-character match is weak evidence: 为
  // is a substring of 因为, so the preposition pairs 对/为 and 给/为 all claimed the question
  // "因为". Two characters agreeing is far more than twice one character agreeing.
  for (const word of entry.words) {
    if (word && query.includes(word)) score += word.length * 8;
  }

  // Bare character overlap, worth almost nothing on its own — a tiebreak between entries that have
  // already matched something real.
  const subject = new Set(entry.words.filter((w) => w.length === 1));
  score += han.filter((c) => subject.has(c)).length * 2;

  // A curated phrasing appearing whole in the question, or the question inside it.
  //
  // Awarded ONCE, however many phrasings match. An entry lists several ways of asking the same
  // thing, so "sentence order", "order" and "grammar" all matching one question is one piece of
  // evidence written three ways, not three pieces — and scoring it three times let an entry that
  // merely shares vocabulary outrank the entry the question named outright.
  const asks = entry.ask.map((a) => a.toLowerCase());
  const lower = query.toLowerCase();
  const phrased = asks.some(
    (ask) => containsPhrase(lower, ask) || (lower.length > 2 && containsPhrase(ask, lower)),
  );
  if (phrased) score += 8;

  // Loose overlap against the phrasings and the title only — never the body, and capped.
  //
  // The cap is what keeps this a tie-breaker rather than a second scoring system. Uncapped, an
  // entry sharing four ordinary words with a question outscored an entry the question named
  // outright in Chinese, which is the wrong way round: naming the thing is the strongest signal a
  // learner can give.
  const bag = new Set(tokenise([...asks, entry.title].join(' ')).words);
  let loose = 0;
  for (const w of words) if (bag.has(w)) loose += 2;
  score += Math.min(loose, LOOSE_CAP);

  return score;
}

/** At most two loose word matches count, so overlap can order results but never choose them. */
const LOOSE_CAP = 4;

/**
 * The score an entry must reach to be shown at all.
 *
 * Set where a single loose word overlap cannot clear it: two shared ordinary words is what an
 * unrelated question looks like, and answering those is how the index starts lying. Anything real —
 * a Chinese character the entry is about, or a phrasing someone curated — clears it easily.
 */
export const FLOOR = 7;

/**
 * How much of an entry is about what was asked.
 *
 * Ties are common and the alphabet is a poor way to break them. Asked "会 能", the modals point and
 * the 会/能 pair scored identically — but the point also covers 可以, while the pair is about those
 * two words and nothing else. The entry with no spare subject matter is the better answer.
 */
function specificity(entry: TutorEntry, query: string): number {
  if (!entry.words.length) return 0;
  const matched = entry.words.filter((w) => w && query.includes(w)).length;
  return matched / entry.words.length;
}

/** Best entries for a query, most relevant first. Empty means the base does not cover it. */
export function search(entries: TutorEntry[], query: string, limit = 3): Hit[] {
  return entries
    .map((entry) => ({ entry, score: scoreEntry(entry, query) }))
    .filter((h) => h.score >= FLOOR)
    .sort(
      (a, b) =>
        b.score - a.score ||
        specificity(b.entry, query) - specificity(a.entry, query) ||
        a.entry.id.localeCompare(b.entry.id),
    )
    .slice(0, limit);
}
